/**
 * What may be typed into an address field, and what may not.
 *
 * These go to eBay, which refuses a listing outright over a malformed address
 * and says so in terms that name no field. A postcode with a stray quote or a
 * semicolon in it is rejected after the seller has finished and walked away, so
 * it is worth catching while they are still looking at the box.
 *
 * Two rules, applied per field:
 *
 *   clean   what the box will accept as it is typed. Characters outside it are
 *           dropped rather than shown as an error, because an error for a key
 *           the seller did not mean to press is noise.
 *   check   whether what is there is usable, once they have finished.
 *
 * Deliberately permissive about shape. A UK postcode, a US ZIP+4, an Indian PIN
 * and an Irish Eircode have nothing in common, and a regex tight enough to
 * validate one rejects the others — which is worse than accepting a typo,
 * because the seller cannot get past it at all. So the rule is about characters,
 * not format: letters, digits, spaces and hyphens, and nothing else.
 */

/** Everything a postcode anywhere plausibly needs, and nothing more. */
const POSTAL_OK = /[^A-Za-z0-9 -]/g
/** Place names carry accents, apostrophes and full stops: St. John's, Zürich. */
const PLACE_OK  = /[^A-Za-zÀ-ÿ0-9 .'\-/()]/g
/** A phone number, in any of the ways one is written. */
const PHONE_OK  = /[^0-9 +()-]/g

const collapse = v => String(v ?? '').replace(/\s+/g, ' ').trimStart()

export const FIELDS = {
  postalCode: {
    label: 'Postcode',
    max: 12,
    clean: v => collapse(v).replace(POSTAL_OK, '').toUpperCase().slice(0, 12),
    check: v => {
      const t = String(v ?? '').trim()
      if (!t) return 'A postcode is required — eBay will not list without one.'
      if (t.length < 3) return 'That looks too short for a postcode.'
      if (!/[A-Za-z0-9]/.test(t)) return 'A postcode needs letters or digits.'
      return null
    },
  },
  city: {
    label: 'City',
    max: 60,
    clean: v => collapse(v).replace(PLACE_OK, '').slice(0, 60),
    check: v => {
      const t = String(v ?? '').trim()
      if (t && t.length < 2) return 'That looks too short for a city.'
      return null
    },
  },
  stateOrProvince: {
    label: 'State / county',
    max: 60,
    clean: v => collapse(v).replace(PLACE_OK, '').slice(0, 60),
    check: () => null,
  },
  addressLine1: {
    label: 'Address',
    max: 120,
    // Commas and hash marks belong in a street address.
    clean: v => collapse(v).replace(/[^A-Za-zÀ-ÿ0-9 .,'\-/#()]/g, '').slice(0, 120),
    check: () => null,
  },
  locationName: {
    label: 'Location name',
    max: 60,
    clean: v => collapse(v).replace(PLACE_OK, '').slice(0, 60),
    check: () => null,
  },
  phone: {
    label: 'Phone',
    max: 20,
    clean: v => collapse(v).replace(PHONE_OK, '').slice(0, 20),
    check: v => {
      const t = String(v ?? '').trim()
      if (t && (t.replace(/\D/g, '').length < 6)) return 'That looks too short for a phone number.'
      return null
    },
  },
}

/** Two letters, as eBay and Shopify both expect a country. */
export const COUNTRY_OK = /^[A-Za-z]{0,2}$/
export const cleanCountry = v => String(v ?? '').replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 2)

/** Strip what the field does not accept. Unknown field: left alone. */
export function clean(field, value) {
  return FIELDS[field] ? FIELDS[field].clean(value) : value
}

/** The problem with this value, or null. Unknown field: no opinion. */
export function check(field, value) {
  return FIELDS[field] ? FIELDS[field].check(value) : null
}

/**
 * Every problem in a set of values, keyed by field.
 *
 * `required` names the fields that must be present even where the field's own
 * rule would let a blank pass — which depends on the form, not on the field.
 */
export function checkAll(values = {}, { required = [] } = {}) {
  const errors = {}
  for (const [field, value] of Object.entries(values)) {
    const problem = check(field, value)
    if (problem) errors[field] = problem
  }
  for (const field of required) {
    if (!errors[field] && String(values[field] ?? '').trim() === '') {
      errors[field] = `${FIELDS[field]?.label || 'This'} is required.`
    }
  }
  return errors
}

/**
 * A saved eBay location that cannot be used, and why.
 *
 * Choosing one from the dropdown skipped every check — its address was eBay's,
 * not something typed here, so it looked like none of our business. It is: that
 * address is what every listing carries, and one saved with a stray character in
 * its postcode was accepted here and then refused at publish time, in a message
 * naming no field at all.
 *
 * Returns null when it is fine.
 */
export function checkSavedLocation(loc) {
  if (!loc) return null
  const fields = {
    postalCode: loc.postal ?? loc.postalCode,
    city: loc.city,
    stateOrProvince: loc.state ?? loc.stateOrProvince,
    addressLine1: loc.line1 ?? loc.addressLine1,
    locationName: loc.name,
  }
  for (const [field, value] of Object.entries(fields)) {
    if (value === undefined || value === null || value === '') continue
    // Cleaning it changes it only if it holds something the field cannot carry.
    if (clean(field, value) !== String(value).trim()) {
      return `${FIELDS[field]?.label || field} contains something eBay will not accept `
           + '— letters, digits, spaces and hyphens only.'
    }
  }
  if (!String(fields.postalCode ?? '').trim()) {
    return 'This location has no postcode, and eBay will not list without one.'
  }
  return null
}
